import classNames from 'classnames';
import { Fragment } from 'react/jsx-runtime';

import './card.scss';

import Icon, { IconType } from '../icon/Icon';
import Link, { LinkVariant } from '../link/Link';
import CheckboxField from '../field/checkbox/CheckboxField';
import Button, { ButtonType } from '../button/Button';
import { TooltipPlacement } from '../tooltip/tooltip';
import CopyCode from '../link/variants/copyCode';

interface TagProps {
  label: string;
  link: string;
}

interface PerformanceAttribute {
  color: string;
  label: string;
  value: number;
  metric?: string;
}

export interface CardProps {
  id: string;
  title: string;
  description?: string;
  image?: {
    src: string;
    alt: string;
  };
  link?: string;
  hasBackground?: boolean;
  horizontal?: boolean;
  extraClass?: string;
  titleClass?: string;
  bodyClass?: string;
  pictogram?: IconType;
  date?: string;
  fallbackImage?: string;
  tags?: TagProps[];
  hasCheckbox?: boolean;
  performanceAttributes?: PerformanceAttribute[];
  specs?: {
    label: string;
    value: string;
    truncate?: boolean;
  }[];
  systemCode?: string;
  footerBtnLabel?: string;
  isBookmarked?: boolean;
  onBookmarkToggle?: (e: React.MouseEvent) => void;
  onButtonBottom?: () => void;
  onSelect?: (system: any, isSelected: boolean) => void;
  onCardClick?: () => void;
  selectedSystems?: any[];
  isLoading?: boolean;
  hoverOverlay?: boolean;
  isLoggedIn?: boolean;
  smartListerLabels?: any;
  card?: any;
}

const Card = ({
  id,
  title,
  description,
  image,
  link,
  hasBackground = true,
  horizontal = false,
  extraClass = '',
  titleClass = 'u-h6',
  bodyClass = 'u-text-small',
  pictogram,
  date,
  fallbackImage = '/assets/images/no-image.jpg',
  tags,
  hasCheckbox = false,
  performanceAttributes = [],
  specs,
  systemCode,
  footerBtnLabel,
  isBookmarked = false,
  onBookmarkToggle,
  onButtonBottom,
  onSelect,
  onCardClick,
  selectedSystems = [],
  isLoading = false,
  hoverOverlay = false,
  isLoggedIn = false,
  smartListerLabels = {},
  card,
}: CardProps) => {
  if (isLoading) {
    return (
      <article className={classNames('m-card o-skel-card', extraClass)}>
        <div className="m-card__media o-skel-card__media o-skel__item">
          &nbsp;
        </div>
        <div className="m-card__body o-skel-card__body">
          <div className="o-skel__item u-h6">&nbsp;</div>
          <div role="group" className="o-skel__paragraph u-text-tiny">
            <div className="o-skel__item">&nbsp;</div>
            <div className="o-skel__item">&nbsp;</div>
            <div className="o-skel__item">&nbsp;</div>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article
      className={classNames('o-basic-card m-card', {
        'u-bg-color-default': hasBackground,
        'm-card--horizontal': horizontal,
        [extraClass]: !!extraClass,
      })}
    >
      {hasCheckbox && (
        <div className="m-card__checkbox">
          <CheckboxField
            id={id}
            label={title}
            hideLabel
            onChange={(e) => {
              if (onSelect) {
                onSelect(card, e.target.checked);
              }
            }}
            checked={selectedSystems.some((s) => s.id === id)}
          />
        </div>
      )}

      {pictogram && (
        <div className="o-basic-card__media m-card__pictogram">
          <Icon iconType={pictogram} className="u-icon-large" />
        </div>
      )}

      {image && !hoverOverlay && (
        <div className="o-basic-card__media m-card__media">
          <img
            className="o-fit"
            src={image.src || fallbackImage}
            alt={image.alt}
          />
        </div>
      )}

      {image && hoverOverlay && (
        <div className="m-card__media-container">
          {link ? (
            <a
              href={link}
              aria-label={`Go to ${title}`}
              className="m-card__media-link"
            >
              <img src={image.src || fallbackImage} alt={image.alt} />
              {systemCode && (
                <CopyCode
                  code={systemCode}
                  smartListerLabels={smartListerLabels}
                />
              )}
              <div className="m-card__text-overlay">
                {title && (
                  <h6 className={classNames('m-card__title', titleClass)}>
                    <span className="u-h3 o-basic-card__overlay m-card__cta">
                      {title}
                    </span>
                  </h6>
                )}
                {description && (
                  <p className="m-card__description">{description}</p>
                )}
              </div>
            </a>
          ) : (
            <>
              <img
                className="o-fit"
                src={image.src || fallbackImage}
                alt={image.alt}
              />
              <div className="m-card__text-overlay">
                {title && <h3 className="m-card__title">{title}</h3>}
                {description && (
                  <p className="m-card__description">{description}</p>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {!hoverOverlay && (
        <div
          className={classNames(
            'o-basic-card__content m-card__body',
            bodyClass
          )}
        >
          {title && (
            <h3
              className={classNames(
                'm-card__title u-truncate-multi',
                titleClass
              )}
            >
              {link ? (
                <a
                  href="#"
                  className="o-basic-card__overlay m-card__cta"
                  aria-label={`Go to ${title}`}
                  onClick={(e) => {
                    e.preventDefault();
                    if (onCardClick) onCardClick();
                  }}
                >
                  {title}
                </a>
              ) : (
                <span>{title}</span>
              )}
            </h3>
          )}

          {systemCode && (
            <CopyCode code={systemCode} smartListerLabels={smartListerLabels} />
          )}

          {description && (
            <div className="m-card__description u-truncate-multi">
              {description}
            </div>
          )}

          {performanceAttributes.length > 0 && (
            <div className="m-card__performance">
              {(performanceAttributes ?? [])
                .filter((pa) => pa.value)
                .map((attribute, index) => (
                  <div key={index} className="m-card__performance-attribute">
                    <span className="performance-attribute-title">
                      {attribute.label}
                    </span>
                    <div
                      className={classNames('performance-attribute-card', {
                        [`performance-attribute-colors--${attribute.color}`]:
                          attribute.color,
                      })}
                    >
                      <span className="performance-attribute-value">
                        {attribute.value}
                      </span>
                      <span className="performance-attribute-metric">
                        {attribute.metric}
                      </span>
                    </div>
                  </div>
                ))}
            </div>
          )}

          {specs && specs.length > 0 && (
            <dl className="o-dl u-label-tiny">
              {specs.map((spec, index) => (
                <Fragment key={index}>
                  <dt>
                    <div
                      className={classNames({
                        'u-truncate-multi u-truncate-multi--1': spec.truncate,
                      })}
                    >
                      {spec.label}
                    </div>
                  </dt>
                  <dd>{spec.value}</dd>
                </Fragment>
              ))}
            </dl>
          )}

          {(tags ?? []).length > 0 && (
            <div className="m-card__tags">
              {(tags ?? []).map((tag, index) => (
                <Link key={index} href={tag.link} label={tag.label} isSmall />
              ))}
            </div>
          )}

          {date && <time className="u-text-tiny">{date}</time>}

          {(footerBtnLabel || onBookmarkToggle) && (
            <footer className="o-basic-card__footer m-card__footer">
              <div className="m-card__footer-inner">
                {footerBtnLabel && (
                  <Button
                    label={footerBtnLabel}
                    icon={IconType.Show}
                    type={ButtonType.TERTIARY}
                    onClick={onButtonBottom}
                    extraClasses="bottom-btn"
                  />
                )}

                {onBookmarkToggle && (
                  <aside className="a-btn-group">
                    <Link
                      variant={LinkVariant.BUTTON}
                      icon={{
                        iconType:
                          isBookmarked && isLoggedIn
                            ? IconType.FavoritesAdded
                            : IconType.Favorites,
                      }}
                      iconTooltip={
                        isLoggedIn
                          ? undefined
                          : {
                              content:
                                'You need to login to use this functionality',
                              placement: TooltipPlacement.RIGHT,
                              interactive: true,
                              maxWidth: 350,
                            }
                      }
                      description={
                        isBookmarked
                          ? 'Remove from bookmarks'
                          : 'Add to bookmarks'
                      }
                      onClick={onBookmarkToggle}
                    />
                  </aside>
                )}
              </div>
            </footer>
          )}
        </div>
      )}
    </article>
  );
};

export default Card;
